import { normalizeNumber } from '@loggi/js/formatters';
import Button from '@loggi/ui/components/button';
import Col from '@loggi/ui/components/col';
import Label from '@loggi/ui/components/label';
import Text, { TEXT_SIZE_SMALL } from '@loggi/ui/components/text';
import colors from '@loggi/ui/components/utils/colors';
import icons from '@loggi/ui/components/utils/icons';
import View from '@loggi/ui/components/view';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { packageTrackerModel } from 'public-tracker/public-tracker.models/package-tracker.model';
import React, { Component } from 'react';
import {
  CLICK_CHANGE_PHONE_NUMBER,
  CLICK_RESEND_TOKEN,
  FAILURE_RESEND_TOKEN,
  SUCCESS_RESEND_TOKEN
} from 'shared/analytics/analytics.constants';
import sendGoogleAnalyticsDataEvent from 'shared/analytics/analytics.utils';
import callMutation from 'shared/relay/mutations/call-mutation';
import mutation from 'shared/relay/mutations/mobile-phone-notification-subscription.mutation';
import { IS_PACKAGE_TRACKING_TOKEN_SCREEN } from 'tracker.constants';
import { PhoneInput } from '../../components/phone-input/phone-input.component';
import statusStyles from '../../status/status.styles.scss';
import VALIDATE_TOKEN from '../token.constants';
import styles from './token-buttons.styles.scss';

const TIMER_VALUE = {
  SECONDS: 59,
  MINUTES: 2
};

class Buttons extends Component {
  static propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    mobilePhoneNumber: PropTypes.string.isRequired,
    shouldStartTimer: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    packageTracker: PropTypes.shape(packageTrackerModel).isRequired,
    updateMobilePhoneNumber: PropTypes.func.isRequired,
    postalCode: PropTypes.string.isRequired,
    callbackToRenderStatusComponent: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    const { shouldStartTimer } = props;
    this._setErrorMessage = this._setErrorMessage.bind(this);
    this._successMutation = this._successMutation.bind(this);
    this._onClickResendToken = this._onClickResendToken.bind(this);
    this._onClickChangePhoneNumber = this._onClickChangePhoneNumber.bind(this);
    this._onClickPhoneInput = this._onClickPhoneInput.bind(this);

    this.state = {
      mobilePhoneNumber: get(this.props, 'mobilePhoneNumber', ''),
      shouldStartTimer,
      minutes: 0,
      seconds: 0,
      hasSentNewToken: false,
      shouldShowInputPhone: false,
      disableResendTokenButton: shouldStartTimer,
      resendTokenError: false
    };
  }

  componentDidMount() {
    const { shouldStartTimer } = this.props;
    if (shouldStartTimer) {
      this._startTimer();
    }
  }

  _sendGoogleAnalyticsEvent = action => {
    sendGoogleAnalyticsDataEvent({ action });
  };

  _formatTimer = (seconds, minutes) => {
    return seconds.toString().length === 1
      ? `0${minutes}:0${seconds}`
      : `0${minutes}:${seconds}`;
  };

  _packageTrackerNotificationSubscriptionMutation() {
    const { mobilePhoneNumber } = this.state;
    const variables = {
      input: {
        packageId: get(
          this.props,
          'packageTracker.packageInfo.packageId',
          null
        ),
        mobileNumber: normalizeNumber(mobilePhoneNumber)
      }
    };
    const callbacks = {
      onSuccess: response => this._successMutation(response),
      onFailure: () => this._setErrorMessage()
    };
    return callMutation(mutation, variables, callbacks);
  }

  _onClickResendToken() {
    this._sendGoogleAnalyticsEvent(CLICK_RESEND_TOKEN);
    return this._packageTrackerNotificationSubscriptionMutation();
  }

  _onClickChangePhoneNumber() {
    this._sendGoogleAnalyticsEvent(CLICK_CHANGE_PHONE_NUMBER);
    this.setState({
      shouldShowInputPhone: true,
      hasSentNewToken: false,
      disableResendTokenButton: true
    });
  }

  _onClickPhoneInput(newMobilePhoneNumber) {
    this.setState(
      {
        mobilePhoneNumber: newMobilePhoneNumber,
        hasSentNewToken: true,
        shouldStartTimer: true,
        disableResendTokenButton: true,
        shouldShowInputPhone: false,
        resendTokenError: false
      },
      () => {
        const { updateMobilePhoneNumber } = this.props;
        clearInterval(this.timer);
        this._startTimer();
        updateMobilePhoneNumber(newMobilePhoneNumber);
      }
    );
  }

  _successMutation(response) {
    const { success } = response.packageTrackerNotificationSubscription;

    if (!success) {
      return this._setErrorMessage();
    }

    this._sendGoogleAnalyticsEvent(SUCCESS_RESEND_TOKEN);

    return this.setState(
      {
        hasSentNewToken: true,
        shouldStartTimer: true,
        disableResendTokenButton: true,
        shouldShowInputPhone: false,
        resendTokenError: false
      },
      () => {
        const { updateMobilePhoneNumber } = this.props;
        const { mobilePhoneNumber } = this.state;
        this._startTimer();
        updateMobilePhoneNumber(mobilePhoneNumber);
      }
    );
  }

  _setErrorMessage() {
    this._sendGoogleAnalyticsEvent(FAILURE_RESEND_TOKEN);
    this.setState({
      hasSentNewToken: true,
      shouldStartTimer: false,
      disableResendTokenButton: false,
      shouldShowInputPhone: false,
      resendTokenError: true
    });
  }

  _startTimer() {
    this.setState(
      {
        minutes: TIMER_VALUE.MINUTES,
        seconds: TIMER_VALUE.SECONDS
      },
      () => {
        this.timer = setInterval(() => this._decrementTimer(), 1000);
      }
    );
  }

  _decrementTimer() {
    const { minutes, seconds } = this.state;

    if (minutes === 0 && seconds === 0) {
      return this.setState(
        {
          shouldStartTimer: false,
          disableResendTokenButton: false,
          hasSentNewToken: false
        },
        () => clearInterval(this.timer)
      );
    }
    return this.setState(prevState => {
      let nextSeconds = prevState.seconds - 1;
      let { nextMinutes } = prevState;
      if (seconds < 0) {
        nextMinutes = prevState.minutes - 1;
        nextSeconds = TIMER_VALUE.SECONDS;
      }
      return { seconds: nextSeconds, minutes: nextMinutes };
    });
  }

  render() {
    const { postalCode, callbackToRenderStatusComponent } = this.props;
    const {
      shouldStartTimer,
      minutes,
      seconds,
      hasSentNewToken,
      disableResendTokenButton,
      shouldShowInputPhone,
      resendTokenError,
      mobilePhoneNumber
    } = this.state;
    const displayTimer = this._formatTimer(seconds, minutes);
    const packageTracker = get(this.props, 'packageTracker', null);

    return (
      <Col
        size={12}
        gutter={{ top: 'half', bottom: 'half' }}
        flexDirection="column"
        alignItems="center"
      >
        <Col gutter={{ bottom: 'half' }}>
          <Text size={TEXT_SIZE_SMALL} color={colors.type.DARK}>
            {IS_PACKAGE_TRACKING_TOKEN_SCREEN.textContent.textPrefix}
            <View inline className={statusStyles.textBolder}>
              {mobilePhoneNumber}
            </View>
            {IS_PACKAGE_TRACKING_TOKEN_SCREEN.textContent.textSufix}
          </Text>
        </Col>
        <Col
          flexDirection="row"
          justifyContent="around"
          gutter={
            !shouldStartTimer && !shouldShowInputPhone
              ? { bottom: 'onehalf' }
              : null
          }
        >
          <Col size={6} gutter={{ right: 'half' }}>
            <Button
              size="small"
              style="outline"
              shape="rounded"
              value={VALIDATE_TOKEN.RESEND_TOKEN}
              onClick={this._onClickResendToken}
              disabled={disableResendTokenButton}
            />
          </Col>
          <Col size={6} gutter={{ left: 'half' }}>
            <Button
              size="small"
              style="outline"
              shape="rounded"
              value={VALIDATE_TOKEN.CHANGE_PHONE_NUMBER}
              onClick={this._onClickChangePhoneNumber}
            />
          </Col>
        </Col>
        {shouldShowInputPhone && (
          <View inline gutter={{ top: 'onehalf' }}>
            <PhoneInput
              packageTracker={packageTracker}
              postalCode={postalCode}
              buttonShape="square"
              buttonIcon={icons.type.CHECK}
              callbackToRenderTokenComponent={this._onClickPhoneInput}
              callbackToRenderStatusComponent={callbackToRenderStatusComponent}
              changeMobilePhoneNumber
              mobilePhoneNumber={mobilePhoneNumber}
            />
          </View>
        )}
        {hasSentNewToken && (
          <Col
            flexDirection="row"
            alignItems="center"
            gutter={{ top: resendTokenError ? null : 'normal' }}
          >
            <Label
              className={styles.successMessageWrapper}
              color={
                resendTokenError ? colors.type.DANGER : colors.type.SUCCESS
              }
              shape="circle"
              icon={resendTokenError ? icons.type.WRONG : icons.type.CHECK}
            />
            <Col flexDirection="column">
              <Text
                data-testid="code-success-message"
                small
                bolder
                color={
                  resendTokenError ? colors.type.DANGER : colors.type.SUCCESS
                }
              >
                {resendTokenError
                  ? VALIDATE_TOKEN.ERROR_MESSAGE.prefixText
                  : VALIDATE_TOKEN.CODE_HAS_BEEN_SENT}
              </Text>
              {resendTokenError && (
                <Text
                  data-testid="code-error-message"
                  small
                  bolder
                  color={colors.type.DANGER}
                >
                  {VALIDATE_TOKEN.ERROR_MESSAGE.sufixText}
                </Text>
              )}
            </Col>
          </Col>
        )}
        {shouldStartTimer && (
          <View
            style={{ textAlign: 'center' }}
            gutter={{
              xs: { top: 'half' },
              md: { top: hasSentNewToken ? 'half' : '' }
            }}
          >
            <Text size={TEXT_SIZE_SMALL} color={colors.type.DARK}>
              {VALIDATE_TOKEN.PREFIX_TIMER_TEXT}
              <Text
                data-testid="timer-text"
                bold
                size={TEXT_SIZE_SMALL}
                color={colors.type.DARKEST}
              >
                {displayTimer}
              </Text>
              {VALIDATE_TOKEN.SUFIX_TIMER_TEXT}
            </Text>
          </View>
        )}
      </Col>
    );
  }
}

export default Buttons;
